import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { services, fadeUp, stagger } from "@/data/landingData";

// Map generic service slugs → dedicated landing pages when available.
// Falls back to the generic /sluzby/:slug route for anything not listed.
const DEDICATED_URLS = {
  "stavby-na-kluc": "/dom-na-kluc",
  "rekonstrukcie": "/rekonstrukcia-bytu",
  "zateplenie": "/zateplenie",
};

export default function Services() {
  return (
    <section
      id="sluzby"
      className="py-24 lg:py-32 section-dark particles"
      data-testid="services"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="mb-16"
        >
          <motion.span
            variants={fadeUp}
            className="mono text-xs text-[#00E5FF] uppercase tracking-[0.2em]"
          >
            Naše služby
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-4"
          >
            Čo pre vás <span className="text-[#FF5E00]">vytvoríme</span>
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          {services.map((service, i) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className={`${service.span} group`}
            >
              <Link
                to={DEDICATED_URLS[service.slug] || `/sluzby/${service.slug}`}
                data-testid={`service-card-${service.slug}`}
                className={`relative block h-full min-h-[280px] glass-card card-hover overflow-hidden ${
                  service.highlight ? "glow-border-cyan" : ""
                } ${service.featured ? "glow-border-orange" : ""}`}
              >
                {service.image && (
                  <div className="absolute inset-0">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-110 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  </div>
                )}

                <div className="relative z-10 p-8 h-full flex flex-col justify-end">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      service.highlight ? "bg-[#00E5FF]/20" : "bg-white/10"
                    }`}
                  >
                    <service.icon
                      size={24}
                      className={service.highlight ? "text-[#00E5FF]" : "text-white"}
                    />
                  </div>

                  <h3 className="text-2xl font-bold mb-2">{service.title}</h3>
                  <p className="text-zinc-400 mb-4 text-sm">{service.description}</p>

                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/10">
                    <span className="mono text-[#00E5FF] text-sm">{service.price}</span>
                    <ArrowUpRight
                      size={18}
                      className="text-zinc-500 group-hover:text-[#00E5FF] transition-colors"
                    />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
